import { useEffect, useRef, useState } from "react"
import { Formik, Field, Form } from "formik"
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    Wrapper,
    ActionSection,
    ScrollableContent,
    SideCancelBtn,
    RupeeIcon,
    ModalHeader,
} from "../Styles/InputStyles"
import { toast } from "react-toastify"
import { MdClose } from "react-icons/md"
import tw from "tailwind-styled-components"
import moment from "moment"
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar } from "react-icons/fa";
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import { FormControl } from "@mui/material"
import { paymentModes } from "../../helpers"
import { IoMdCheckmarkCircleOutline } from "react-icons/io"

// Staff ledger types constants
const STAFF_LEDGER_TYPES = {
    EXPENSE_RETURN: 5,
    BONUS: 6,
    REIMBURSEMENT: 7,
    SALARY_ADVANCE: 8,
    TRAVEL_ALLOWANCE: 9,
    HRA: 10
};

const AddPayable = ({
    handleClose,
    addPayableMutate,
    addPayableLoading,
    staffId,
}) => {
    const formikRef = useRef(null)
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [dueDateCalendarOpen, setDueDateCalendarOpen] = useState(false);
    const [height, setHeight] = useState(window.innerHeight);

    // Staff ledger type options for dropdown
    const staffLedgerTypeOptions = [
        { value: STAFF_LEDGER_TYPES.BONUS, label: "Bonus" },
        { value: STAFF_LEDGER_TYPES.REIMBURSEMENT, label: "Reimbursement" },
        { value: STAFF_LEDGER_TYPES.SALARY_ADVANCE, label: "Advance Salary" },
        { value: STAFF_LEDGER_TYPES.EXPENSE_RETURN, label: "Expense Advance" },
        { value: STAFF_LEDGER_TYPES.TRAVEL_ALLOWANCE, label: "Travel Allowance" },
        { value: STAFF_LEDGER_TYPES.HRA, label: "HRA" },
    ];

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const initialValues = {
        ledgerType: "",
        amount: "",
        dueDate: "",
        paidDate: moment().format("YYYY-MM-DD"),
        paymentMode: "",
        transactionId: "",
        description: "",
    }

    const handleSubmit = (values) => {
        // Validate amount
        const amountRegex = /^(?!0+$)\d{1,7}$/
        const maxAmount = 5000000

        if (!values.ledgerType) {
            toast.error("Please select a type")
            return
        }

        if (!amountRegex.test(values.amount) || Number.parseInt(values.amount) > maxAmount) {
            toast.error("Amount must be between 1 and 50,00,000 and contain only numbers")
            return
        }

        // Validate based on ledger type
        const ledgerType = Number(values.ledgerType);

        // Type-specific validations
        if (ledgerType === STAFF_LEDGER_TYPES.BONUS ||
            ledgerType === STAFF_LEDGER_TYPES.TRAVEL_ALLOWANCE ||
            ledgerType === STAFF_LEDGER_TYPES.HRA) {
            if (!values.dueDate) {
                toast.error("Due date is required")
                return
            }
        }

        if (ledgerType === STAFF_LEDGER_TYPES.REIMBURSEMENT ||
            ledgerType === STAFF_LEDGER_TYPES.SALARY_ADVANCE ||
            ledgerType === STAFF_LEDGER_TYPES.EXPENSE_RETURN) {
            if (!values.paidDate) {
                toast.error("Paid date is required")
                return
            }
            if (!values.paymentMode) {
                toast.error("Payment mode is required")
                return
            }
        }

        if (ledgerType === STAFF_LEDGER_TYPES.SALARY_ADVANCE) {
            if (!values.dueDate) {
                toast.error("Due date is required")
                return
            }
        }

        addPayableMutate({
            type: Number(values.ledgerType),
            amount: Number(values.amount),
            description: values.description || "",
            staffId: staffId,

            ...(values.dueDate && {
                dueDate: moment(values.dueDate).format("YYYY-MM-DD"),
            }),

            ...(values.paidDate && {
                paidDate: moment(values.paidDate).format("YYYY-MM-DD"),
            }),

            ...(values.paymentMode && {
                mode: Number(values.paymentMode),
            }),

            ...(values.transactionId && {
                transId: values.transactionId,
            }),
        });
    }
    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Add Payable</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, setFieldValue, handleChange }) => {
                        // Get current ledger type for conditional rendering
                        const currentLedgerType = Number(values.ledgerType || 0);

                        // Define conditional flags based on current ledger type
                        const showDueDate = [
                            STAFF_LEDGER_TYPES.BONUS,
                            STAFF_LEDGER_TYPES.TRAVEL_ALLOWANCE,
                            STAFF_LEDGER_TYPES.HRA,
                            STAFF_LEDGER_TYPES.SALARY_ADVANCE
                        ].includes(currentLedgerType);

                        const showPaidDate = [
                            STAFF_LEDGER_TYPES.REIMBURSEMENT,
                            STAFF_LEDGER_TYPES.EXPENSE_RETURN,
                            STAFF_LEDGER_TYPES.SALARY_ADVANCE
                        ].includes(currentLedgerType);

                        const showPaymentMode = [
                            STAFF_LEDGER_TYPES.REIMBURSEMENT,
                            STAFF_LEDGER_TYPES.EXPENSE_RETURN,
                            STAFF_LEDGER_TYPES.SALARY_ADVANCE
                        ].includes(currentLedgerType);

                        const showTransactionId = [
                            STAFF_LEDGER_TYPES.REIMBURSEMENT,
                            STAFF_LEDGER_TYPES.EXPENSE_RETURN,
                            STAFF_LEDGER_TYPES.SALARY_ADVANCE
                        ].includes(currentLedgerType);

                        const autoPaidTypes = [
                            STAFF_LEDGER_TYPES.REIMBURSEMENT,
                            STAFF_LEDGER_TYPES.SALARY_ADVANCE,
                            STAFF_LEDGER_TYPES.EXPENSE_RETURN,
                        ].includes(currentLedgerType);

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        {/* Ledger Type Selection - MUI Select */}
                                        <InputGroup>
                                            <Label htmlFor="ledgerType">Type *</Label>
                                            <FormControl fullWidth>
                                                <Select
                                                    id="ledgerType"
                                                    name="ledgerType"
                                                    value={values.ledgerType}
                                                    onChange={(e) => {
                                                        setFieldValue("ledgerType", e.target.value);
                                                        setFieldValue("dueDate", "");
                                                        setFieldValue("paidDate", moment().format("YYYY-MM-DD"));
                                                        setFieldValue("paymentMode", "");
                                                        setFieldValue("transactionId", "");
                                                    }}
                                                    style={{ height: "46px" }}
                                                    displayEmpty
                                                    className="pl-2"
                                                    renderValue={(selected) => {
                                                        if (!selected) {
                                                            return <span className="text-gray-400">Select type</span>;
                                                        }
                                                        const option = staffLedgerTypeOptions.find(opt => opt.value === selected);
                                                        return option ? option.label : selected;
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        <span className="text-gray-400">Select type</span>
                                                    </MenuItem>
                                                    {staffLedgerTypeOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {autoPaidTypes && (
                                                <div className="flex items-start gap-2 text-xs text-primary-color bg-primary-color/10 border border-primary-color/20 rounded px-3 py-2">
                                                    <span className="mt-0.5"><IoMdCheckmarkCircleOutline /></span>
                                                    <span>This entry will be automatically marked as paid.</span>
                                                </div>
                                            )}
                                        </InputGroup>

                                        {/* Amount - Common for all types */}
                                        <InputGroup>
                                            <Label htmlFor="amount">Amount *</Label>
                                            <FieldWrappers>
                                                <RupeeIcon />
                                                <Field
                                                    type="text"
                                                    name="amount"
                                                    id="amount"
                                                    className="w-full truncate pl-5"
                                                    placeholder="Enter amount"
                                                />
                                            </FieldWrappers>
                                        </InputGroup>

                                        {/* Due Date - For Bonus, Travel Allowance, HRA, Salary Advance */}
                                        {showDueDate && (
                                            <InputGroup>
                                                <Label htmlFor="dueDate">Due Date *</Label>
                                                <FieldWrappers className="relative">
                                                    <DatePicker
                                                        // selected={values.dueDate ? new Date(values.dueDate) : null}
                                                        selected={values.dueDate ? moment(values.dueDate, "YYYY-MM-DD").toDate() : null}
                                                        onChange={(date) => {
                                                            const formattedDate = moment(date).format("YYYY-MM-DD");
                                                            setFieldValue("dueDate", formattedDate);
                                                            setDueDateCalendarOpen(false);
                                                        }}
                                                        onClickOutside={() => setDueDateCalendarOpen(false)}
                                                        onSelect={() => setDueDateCalendarOpen(false)}
                                                        open={dueDateCalendarOpen}
                                                        onInputClick={() => setDueDateCalendarOpen(true)}
                                                        dateFormat="yyyy-MM-dd"
                                                        className="w-full truncate cursor-pointer"
                                                        placeholderText="Select due date"
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        showPopperArrow={false}
                                                        onFocus={(e) => e.target.blur()}
                                                        wrapperClassName="w-full"
                                                        name="dueDate"
                                                        id="dueDate"
                                                        autoComplete="off"
                                                        popperPlacement="bottom-start"
                                                        portalId="root-portal"
                                                        //minDate={new Date()}
                                                        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                                                    />
                                                </FieldWrappers>
                                            </InputGroup>
                                        )}

                                        {/* Paid Date - For Reimbursement, Salary Advance */}
                                        {showPaidDate && (
                                            <InputGroup>
                                                <Label htmlFor="paidDate">Paid Date *</Label>
                                                <FieldWrappers className="relative">
                                                    <DatePicker
                                                        //selected={values.paidDate ? new Date(values.paidDate) : new Date()}
                                                        //selected={values.paidDate ? moment(values.paidDate, "YYYY-MM-DD").toDate() : new Date()}
                                                        selected={values.paidDate ? moment(values.paidDate, "YYYY-MM-DD").toDate() : new Date()}
                                                        onChange={(date) => {
                                                            const formattedDate = moment(date).format("YYYY-MM-DD");
                                                            setFieldValue("paidDate", formattedDate);
                                                            setCalendarOpen(false);
                                                        }}
                                                        onClickOutside={() => setCalendarOpen(false)}
                                                        onSelect={() => setCalendarOpen(false)}
                                                        open={calendarOpen}
                                                        onInputClick={() => setCalendarOpen(true)}
                                                        dateFormat="yyyy-MM-dd"
                                                        className="w-full truncate cursor-pointer"
                                                        placeholderText="YYYY-MM-DD"
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        showPopperArrow={false}
                                                        onFocus={(e) => e.target.blur()}
                                                        wrapperClassName="w-full"
                                                        name="paidDate"
                                                        id="paidDate"
                                                        autoComplete="off"
                                                        popperPlacement="bottom-start"
                                                        portalId="root-portal"
                                                        maxDate={new Date()}
                                                    />
                                                </FieldWrappers>
                                            </InputGroup>
                                        )}

                                        {/* Payment Mode and Transaction ID - For Reimbursement, Salary Advance */}
                                        {showPaymentMode && (
                                            <>
                                                <InputGroup>
                                                    <Label>Payment Mode *</Label>
                                                    <PaymentModeWrapper>
                                                        {paymentModes.map((mode) => (
                                                            <PaymentModeCard
                                                                key={mode.value}
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    setFieldValue("paymentMode", mode.value)
                                                                }}
                                                                className={`${values.paymentMode === mode.value ? "bg-primary-color text-white" : "bg-gray-100"
                                                                    } `}
                                                            >
                                                                <span className="text-sm">{mode.label}</span>
                                                            </PaymentModeCard>
                                                        ))}
                                                    </PaymentModeWrapper>
                                                </InputGroup>

                                                {showTransactionId && (
                                                    <InputGroup>
                                                        <Label htmlFor="transactionId">Transaction ID </Label>
                                                        <FieldWrappers>
                                                            <Field
                                                                type="text"
                                                                name="transactionId"
                                                                id="transactionId"
                                                                className="w-full truncate"
                                                                placeholder="Enter transaction ID"
                                                            />
                                                        </FieldWrappers>
                                                    </InputGroup>
                                                )}
                                            </>
                                        )}
                                        {/* Description - Common for all types */}
                                        <InputGroup>
                                            <Label htmlFor="description">Description </Label>
                                            <FieldWrapper>
                                                <Field
                                                    as="textarea"
                                                    name="description"
                                                    id="description"
                                                    className="w-full min-h-[80px] p-2 border border-gray-300 rounded resize-y"
                                                    placeholder="Enter description"
                                                />
                                            </FieldWrapper>
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleClose}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={addPayableLoading}>
                                        {addPayableLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
        </Container>
    )
}

const FieldWrapper = tw.div`w-full`;
const Container = tw.div`bg-white w-full h-full flex flex-col relative`
const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`
const PaymentModeWrapper = tw.div`grid grid-cols-4 gap-2 my-1.5`
const PaymentModeCard = tw.button`flex flex-col items-center justify-center p-2 border rounded-lg shadow-sm cursor-pointer`

export default AddPayable